/**
 * Starting price for a course or a category, derived from the package data the
 * team public page already receives.
 *
 * The trial package is deliberately excluded from the starting price: a trial
 * is a different lesson length (25 minutes against 90 on Peak Academy), so its
 * unit price is not comparable and showing it as the entry price understates
 * the real one. The trial is returned alongside instead, for its own line.
 */

const EMPTY = Object.freeze({
  groupSize: null,
  startingPrice: null,
  trialPrice: null,
  currency: null,
  durationMinutes: null,
  startingPackage: null,
  trialPackage: null
})

function usablePrice(value) {
  const numeric = typeof value === 'string' ? parseFloat(value) : value
  return typeof numeric === 'number' && !isNaN(numeric) ? numeric : null
}

/**
 * @param {Array} packages - serialized course packages ({ unitPrice, isTrial, currency, durationMinutes })
 * @returns {{ startingPrice: number|null, trialPrice: number|null, currency: string|null, durationMinutes: number|null }}
 */
export function startingPriceFor(packages) {
  if (!Array.isArray(packages) || packages.length === 0) return { ...EMPTY }

  let cheapestRegular = null
  let cheapestTrial = null

  // A group package's price is for the whole lesson. One student's starting
  // price reads one-to-one packages; only a course that sells nothing else
  // falls back to the price per person, and groupSize says so.
  const regular = packages.filter(pkg => !pkg?.isTrial)
  const solo = regular.filter(pkg => (pkg?.participants || 1) === 1)
  const groupOnly = solo.length === 0 && regular.length > 0
  const groupSize = groupOnly ? Math.min(...regular.map(pkg => pkg.participants)) : null
  const candidates = [
    ...packages.filter(pkg => pkg?.isTrial),
    ...(groupOnly
      ? regular.map(pkg => ({ ...pkg, unitPrice: pkg.perPersonPrice ?? usablePrice(pkg.unitPrice) / pkg.participants, source: pkg }))
      : solo)
  ]

  candidates.forEach((pkg) => {
    const price = usablePrice(pkg?.unitPrice)
    if (price === null) return

    if (pkg.isTrial) {
      if (cheapestTrial === null || price < cheapestTrial.unitPrice) {
        cheapestTrial = { ...pkg, unitPrice: price, source: pkg }
      }
      return
    }

    // A zero-priced regular package is almost always a teacher who has not set
    // a price yet, but the package modal lists it as NT$0 either way. The card
    // shows the same figure rather than going silent on a row the modal sells.
    if (cheapestRegular === null || price < cheapestRegular.unitPrice) {
      cheapestRegular = { ...pkg, unitPrice: price, source: pkg.source || pkg }
    }
  })

  // A team may price courses in different currencies. Two figures printed side
  // by side share one symbol, so a trial in another currency is dropped rather
  // than relabelled — ¥500 shown as NT$500 misstates the price outright.
  const currency = cheapestRegular?.currency || cheapestTrial?.currency || null
  const trialMatchesCurrency = cheapestTrial !== null &&
    (cheapestRegular === null || cheapestTrial.currency === cheapestRegular.currency)

  return {
    startingPrice: cheapestRegular ? cheapestRegular.unitPrice : null,
    trialPrice: trialMatchesCurrency ? cheapestTrial.unitPrice : null,
    currency,
    durationMinutes: cheapestRegular?.durationMinutes ?? null,
    // The original package objects, so a caller that makes these figures
    // tappable can hand the real package to the purchase flow.
    startingPackage: cheapestRegular ? cheapestRegular.source : null,
    trialPackage: trialMatchesCurrency ? cheapestTrial.source : null,
    groupSize: cheapestRegular ? groupSize : null
  }
}

/**
 * The team's entry price, for the hero anchor.
 *
 * Deliberately not "cheapest trial anywhere plus cheapest lesson anywhere":
 * printed side by side, two figures from two courses read as one offer. On
 * iLake the free trial belongs to a NT$900 course while the NT$100 price
 * belongs to a course with no trial at all, so that pairing advertises
 * something nobody sells. Both figures come from one course instead.
 *
 * @param {Array} courses - the full courses payload
 */
export function entryPriceForTeam(courses) {
  if (!Array.isArray(courses) || courses.length === 0) return { ...EMPTY, courseName: null, course: null }

  let best = null
  // A team that has only listed a trial so far is exactly the team that most
  // needs a figure in the hero, so it is kept as a fallback.
  let trialOnly = null

  courses.forEach((course) => {
    const price = startingPriceFor(course?.packages)
    if (price.startingPrice === null) {
      if (trialOnly === null && typeof price.trialPrice === 'number') {
        trialOnly = { price, course }
      }
      return
    }
    if (best === null || price.startingPrice < best.price.startingPrice) {
      best = { price, course }
    }
  })

  if (best === null) best = trialOnly
  if (best === null) return { ...EMPTY, courseName: null, course: null }

  return { ...best.price, courseName: best.course?.name || null, course: best.course }
}

/**
 * Category cards carry `courseIds` rather than packages. The courses payload is
 * not filtered by category, so every referenced course is present and the
 * lowest price across them can be resolved on the client.
 *
 * @param {Array<number>} courseIds
 * @param {Array} courses - the full courses payload
 */
export function startingPriceForCourseIds(courseIds, courses) {
  if (!Array.isArray(courseIds) || courseIds.length === 0) return { ...EMPTY }
  if (!Array.isArray(courses) || courses.length === 0) return { ...EMPTY }

  // Delegates to entryPriceForTeam rather than flattening every package in the
  // category: flattening reintroduces exactly the cross-course pairing that
  // entryPriceForTeam exists to prevent.
  const wanted = new Set(courseIds)
  return entryPriceForTeam(courses.filter((course) => wanted.has(course?.id)))
}
